import { Record } from 'immutable';

import Hydrator from 'utils/Hydrator';

import Lender from 'models/LenderModel';
import Likelihood from 'models/LikelihoodModel';
import ResponseTime from 'models/ResponseTimeModel';
import { QuestionnaireType, QuestionnaireTypeByProductType } from 'enums/questionnaire-type';
import { ProductTypeLabel, ProductType, ClientType } from 'enums/product-type';
import { dateFormat } from 'utils/DateHelper';

const StubebdInsightProductRecord = Record({
  id: 0,
  name: '',
  type: '',
  productType: null,
  description: '',
  likelihood: null,
  responseTime: null,
  providers: [],
  createdAt: new Date(0),
  metadata: null,
  capitalType: '',
});

export default class StubbedInsightProduct extends StubebdInsightProductRecord {
  static deserialize(jsonObject = {}) {
    const mapProperties = {
      createdAt: Hydrator.timestamp2Date,
      responseTime: ResponseTime.deserialize,
      providers: Hydrator.fromList(Lender.deserialize),
      likelihood: Likelihood.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new StubbedInsightProduct(hydrated);
  }

  get questionnaireType() {
    return (
      QuestionnaireTypeByProductType[this.id] || QuestionnaireType.introducerAllFundingProducts
    );
  }

  // calculate questionnaire payload for pre-filling it with answers
  // values hardcoded could change in the future (for example GBP)
  // or any major change in the BE
  getQuestionnairePayload(type, companyDetails, preview, insightName, loanAmount) {
    const FUNDING_FIELDS = {
      legalRequirements: true,
      inviteClient: false,
      fundingAmount: { code: 'GBP', value: loanAmount },
    };

    const useOfFundsAllProducts = () => {
      if (insightName === 'High Fixed Assets') return 'EQUIPMENT_ASSET_FINANCE';
      if (insightName === 'Vat liability' || insightName === 'Tax liability') return 'TAX_BILL_DUE';
      return 'CASH_FLOW';
    };

    const SPECIFIC_FIELDS_BY_TYPE = {
      [QuestionnaireType.introducerAllFundingProducts]: {
        fundingPeriod: this.name === ProductTypeLabel[ProductType.invoiceFinancing] ? 12 : 60,
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useOfFunds: useOfFundsAllProducts(),
        fundingUrgency: 'ASAP',
        raiseInvoices: this.name === ProductTypeLabel[ProductType.invoiceFinancing],
        cardPayments: false,
        fundingAsset: this.name === ProductTypeLabel[ProductType.assetBackedFinance],
        assetType:
          this.name === ProductTypeLabel[ProductType.assetBackedFinance]
            ? 'MANUFACTURING'
            : undefined,
        purchaseOrders: false,
        governmentBacked: false,
        clientType:
          this.name === ProductTypeLabel[ProductType.invoiceFinancing]
            ? ClientType.LARGE_CORPORATES
            : undefined,
        ...FUNDING_FIELDS,
      },
      [QuestionnaireType.introducerRecoveryLoanScheme]: {
        fundingPeriod: 60,
        useOfFunds: 'CASH_FLOW',
        fundingUrgency: 'ASAP',
        raiseInvoices: false,
        cardPayments: false,
        fundingAsset: false,
        purchaseOrders: false,
        ...FUNDING_FIELDS,
      },
      [QuestionnaireType.introducerProperty]: {
        fundingPeriod: 240,
        refinancing: true,
        ...FUNDING_FIELDS,
      },
      [QuestionnaireType.introducerWorkingCapital]: {
        fundingPeriod: 60,
        useOfFunds: 'CASH_FLOW',
        fundingUrgency: 'ASAP',
        ...FUNDING_FIELDS,
      },
      [QuestionnaireType.introducerRecovery]: {
        claimAmount: { code: 'GBP', value: loanAmount },
      },
    };

    return {
      companyType: 'LIMITED_COMPANY',
      companyNumber: companyDetails?.registrationRef,
      companyName: companyDetails?.companyName,
      sector: companyDetails?.normalizedSector,
      incorporationDate: companyDetails?.registrationDate
        ? dateFormat(new Date(companyDetails.registrationDate), 'yyyy-mm-dd')
        : null,
      ...(type !== QuestionnaireType.introducerRecovery &&
        preview.annualRevenue && {
          revenue: { code: 'GBP', value: preview.annualRevenue },
        }),
      ...(type !== QuestionnaireType.introducerRecovery &&
        preview.annualProfitNet && {
          netProfit: { code: 'GBP', value: preview.annualProfitNet },
        }),
      ...SPECIFIC_FIELDS_BY_TYPE[type],
    };
  }
}
